/* @flow */

import type { ReadOnlyKeyValuePair } from '../../../../helpers/jsHelpers/types';
import type { NETGEM_API_V8_RAW_FEED } from './FeedResult';
import type { NETGEM_API_V8_MATCHING_SCHEDULED_RECORDING, RecordingOutcome } from './Npvr';
import type { NETGEM_API_V8_PARENTAL_GUIDANCE } from './ParentalGuidance';

import { logWarning } from '../../../../helpers/debug/debug';

// Raw item description (as received from platform)

export type NETGEM_API_V8_ITEM_SCORE = Array<number | string>;

export type NETGEM_API_V8_ITEM_LOCATION = {
  availabilityEndDate?: string,
  availabilityStartDate?: string,
  channelId?: string,
  duration?: string,
  id: string,
  matchingScheduledRecordings?: Array<NETGEM_API_V8_MATCHING_SCHEDULED_RECORDING>,
  parentalGuidance?: NETGEM_API_V8_PARENTAL_GUIDANCE,
  previewAvailabilityStartDate?: string,
  quotaUsed?: string,
  recordOutcome?: RecordingOutcome,
  scheduledEventDuration?: string,
  scheduledEventStartDate?: string,
  score: NETGEM_API_V8_ITEM_SCORE,
  startDate?: string,
};

export type NETGEM_API_V8_FEED_RAW_ITEM = {
  elements?: NETGEM_API_V8_RAW_FEED,
  id: string,
  locations?: Array<NETGEM_API_V8_ITEM_LOCATION>,
  parentalGuidance?: NETGEM_API_V8_PARENTAL_GUIDANCE,
  purchasable?: boolean,
  score?: NETGEM_API_V8_ITEM_SCORE,
};

export const ItemLocationKind: ReadOnlyKeyValuePair<string> = Object.freeze({
  App: 'app',
  Catchup: 'catchup',
  EST: 'est',
  Game: 'game',
  Recording: 'recording',
  ScheduledEvent: 'scheduledevent',
  SVOD: 'svod',
  TVOD: 'tvod',
  VOD: 'vod',
});

// Cannot convert to Flow enum because it's used as an object's key
export type NetgemApiV8ItemLocation = 'app' | 'catchup' | 'est' | 'game' | 'recording' | 'svod' | 'scheduledevent' | 'tvod' | 'vod';

export const stringToLocationType = (locType: string): NetgemApiV8ItemLocation => {
  switch (locType) {
    case ItemLocationKind.App:
      return 'app';
    case ItemLocationKind.Catchup:
      return 'catchup';
    case ItemLocationKind.EST:
      return 'est';
    case ItemLocationKind.Game:
      return 'game';
    case ItemLocationKind.Recording:
      return 'recording';
    case ItemLocationKind.SVOD:
      return 'svod';
    case ItemLocationKind.ScheduledEvent:
      return 'scheduledevent';
    case ItemLocationKind.TVOD:
      return 'tvod';
    case ItemLocationKind.VOD:
      return 'vod';
    default:
      logWarning(`Unexpected location type: ${locType}`);
      return 'app';
  }
};

export enum ItemContent {
  Game = 'game',
  NetgemSVOD = 'netgem_svod',
  TV = 'tv',
  Unknown = 'unknown',
  VODOrDeeplink = 'vod_or_deeplink',
}

export enum VodKind {
  EST = 'est',
  SVOD = 'svod',
  TVOD = 'tvod',
  VOD = 'vod',
}

// Specify if item is related to a 'grouped' content (ex: series, season, ...) or not (ex: single program)
export enum ItemType {
  Channel = 'channel',
  ChannelGroup = 'channelgr',
  FakeItem = 'fakeitem',
  Program = 'program',
  Series = 'series',
  Universe = 'univers',
}

export type NETGEM_API_V8_FEED_ITEM = {
  ...NETGEM_API_V8_FEED_RAW_ITEM,
  endTime: number,
  locType: ?NetgemApiV8ItemLocation,
  selectedLocation: NETGEM_API_V8_ITEM_LOCATION,
  selectedProgramId: string,
  seriesId: ?string,
  startTime: number,
  type: ?ItemType,
};

export type NETGEM_API_V8_FEED = Array<NETGEM_API_V8_FEED_ITEM>;

export type NETGEM_API_V8_MIXED_FEED = Array<NETGEM_API_V8_FEED_ITEM | NETGEM_API_V8_FEED_RAW_ITEM>;
